/**
 * @file This file export all the event trackers that can be used in the applications.
 *
 * The concrete implementation can be found in the specific provider files:
 * - {@link `amplitude/events.ts`}
 * - {@link `braze/events.ts`}
 * - {@link `google-tag-manager/events/events.ts`} (⚠️ deprecated, being migrated)
 * - {@link `google-tag-manager/events/staging/events.ts`} (🚧 new events during migration)
 * - {@link `noodle/events.ts`}
 *
 * If you want to track with a single provider, you need to:
 * 1. Add the event to the corresponding provider file (e.g. `amplitude/events.ts`)
 * 2. Expose the event in this file (e.g. `export { trackXYZ } from './amplitude/events'`)
 *
 * If you want to track the event in multiple providers, you need to:
 * 1. Add the event to the corresponding providers files (e.g. `amplitude/events.ts` and `noodle/events.ts`)
 * 2. Create a new function in this file that calls the event in all the providers (see: `trackClick`)
 *
 * GTM MIGRATION :
 * - New GTM events go to `google-tag-manager/events/staging/events.ts` first (console logs)
 * - Once 100% validated, move to `google-tag-manager/events/events.ts` (connects to real GTM)
 * - Update imports below from `googleTagManagerStaging` to `googleTagManager`
 * @see google-tag-manager/README.md
 */

import { scheduleWhenIdle as whenIdle } from '@backmarket/utils/performance/scheduleWhenIdle'

import {
  setOptIn as setOptInOnAmplitude,
  setUserProperties as setUserPropertiesOnAmplitude,
} from './amplitude'
import * as amplitude from './amplitude/events'
import { setOptIn as setOptInOnBraze } from './braze'
import * as braze from './braze/events'
import * as googleTagManager from './google-tag-manager/events/events'
import * as googleTagManagerStaging from './google-tag-manager/events/staging/events'
import type {
  AddToFavoriteParams,
  SelectPromotionParams,
  ViewItemParams,
} from './google-tag-manager/events/staging/events'
import * as noodle from './noodle/events'
import type {
  AddToCartParams,
  CarouselData,
  EventData,
  GDPRData,
  ModalData,
  ResizeData,
  SearchAnalyticsData,
  TrackPageViewParams,
} from './types'

/* Expose single provider events */

export const trackSearchTerm = whenIdle(braze.trackSearchTerm)
export const trackBuybackDeviceSpecifications = whenIdle(
  braze.trackBuybackDeviceSpecifications,
)
export const trackBuyBackPlayStationPartnership = whenIdle(
  braze.trackBuyBackPlayStationPartnership,
)

export const trackShoppingAssistantChatReady = whenIdle(
  braze.trackShoppingAssistantChatReady,
)
export const trackShoppingAssistantProductClick = whenIdle(
  braze.trackShoppingAssistantProductClick,
)

export const trackShoppingAssistantProductRecommendationReceived = whenIdle(
  braze.trackShoppingAssistantProductRecommendationReceived,
)

export const trackShoppingAssistantMessageSent = whenIdle(
  braze.trackShoppingAssistantMessageSent,
)

export const trackToast = whenIdle(googleTagManager.trackToast)
export const trackBuybackNoOffer = whenIdle(
  googleTagManager.trackBuybackNoOffer,
)
export const trackBuybackConfirmation = whenIdle(
  googleTagManager.trackBuybackConfirmation,
)
export const trackSwapModal = whenIdle(googleTagManager.trackSwapModal)
export const trackSwap = whenIdle(googleTagManager.trackSwap)
export const trackPaymentEvent = whenIdle(noodle.trackPaymentEvent)
export const trackPartnershipBannerImpression = whenIdle(
  amplitude.trackPartnershipBannerImpression,
)
export const trackSupportAiFeedbackEligibleResponseReceived = whenIdle(
  amplitude.trackSupportAiFeedbackEligibleResponseReceived,
)
export const trackSupportAiError = whenIdle(amplitude.trackSupportAiError)
export const trackSupportAiNoAnswer = whenIdle(amplitude.trackSupportAiNoAnswer)

/* Aggregate events from multiple provider */

export const trackClick = whenIdle((payload: EventData) => {
  amplitude.trackClick(payload)
  googleTagManager.trackClick(payload)
})

export const trackChange = whenIdle((payload: EventData) => {
  amplitude.trackChange(payload)
})

export const trackPageView = whenIdle(({ to, from }: TrackPageViewParams) => {
  amplitude.trackPageView({ from, to })
  googleTagManager.trackPageView(to)
  // 🚧 During GTM migration, also log to console from staging implementation for validation before connecting to real GTM.
  googleTagManagerStaging.trackPageView(to)
})

export const trackViewSearchResults = whenIdle(
  ({ searchTerm }: { searchTerm: string }) => {
    googleTagManagerStaging.trackViewSearchResults({ searchTerm })
  },
)

export const trackSelectPromotion = whenIdle(
  (params: SelectPromotionParams) => {
    googleTagManagerStaging.trackSelectPromotion(params)
  },
)

export const trackViewCart = whenIdle((params: EventData) => {
  googleTagManagerStaging.trackViewCart(params)
})

export const trackAddShippingInfo = whenIdle((params: EventData) => {
  googleTagManagerStaging.trackAddShippingInfo(params)
})

export const trackViewItem = whenIdle((params: ViewItemParams) => {
  googleTagManagerStaging.trackViewItem(params)
})

export const trackBeginCheckout = whenIdle((params: EventData) => {
  googleTagManagerStaging.trackBeginCheckout(params)
})

export const trackAddPaymentInfo = whenIdle((params: EventData) => {
  googleTagManagerStaging.trackAddPaymentInfo(params)
})

export const trackPurchase = whenIdle((params: EventData) => {
  googleTagManagerStaging.trackPurchase(params)
})

export const trackAddToFavorite = whenIdle((params: AddToFavoriteParams) => {
  googleTagManagerStaging.trackAddToFavorite(params)
})

export const trackAddToCartGa4 = whenIdle((params: AddToCartParams) => {
  googleTagManagerStaging.trackAddToCart(params)
})

export const trackScreenView = whenIdle((payload: EventData) => {
  amplitude.trackScreenView(payload)
})

export const trackModal = whenIdle((payload: ModalData) => {
  amplitude.trackModal(payload)
  googleTagManager.trackModal(payload)
})

export const trackResize = whenIdle((data: ResizeData) => {
  setUserPropertiesOnAmplitude({ breakpoint: data.breakpointValue })
  googleTagManager.trackResize(data)
})

export const trackGDPR = whenIdle((data: GDPRData) => {
  setOptInOnAmplitude(data.analytics)
  void setOptInOnBraze(data.userExperience && data.advertising)
  googleTagManager.trackGDPR(data)
})

export const trackFormSubmit = whenIdle((payload: EventData) => {
  amplitude.trackFormSubmit(payload)
  googleTagManager.trackFormSubmit(payload)
})

export const trackSearchAnalytics = whenIdle((data: SearchAnalyticsData) => {
  noodle.trackSearchAnalytics(data)
})

export const trackHoverOver = whenIdle((payload: EventData) => {
  amplitude.trackHoverOver(payload)
})

export const trackVideosBlockImpression = whenIdle((payload: EventData) => {
  amplitude.trackVideosBlockImpression(payload)
})

export const trackVideoImpression = whenIdle((payload: EventData) => {
  amplitude.trackVideoImpression(payload)
})

export const trackReviewImpression = whenIdle((payload: EventData) => {
  amplitude.trackReviewImpression(payload)
})

export const trackReviewerInfoImpression = whenIdle((payload: EventData) => {
  amplitude.trackReviewerInfoImpression(payload)
})

export const trackReviewBlockImpression = whenIdle((payload: EventData) => {
  amplitude.trackReviewBlockImpression(payload)
})

export const trackReviewCarouselImpression = whenIdle((payload: EventData) => {
  amplitude.trackReviewCarouselImpression(payload)
})

export const trackReviewListImpression = whenIdle((payload: EventData) => {
  amplitude.trackReviewListImpression(payload)
})

export const trackProductImpressionBatched = whenIdle(
  (payload: EventData[]) => {
    amplitude.trackProductImpressionBatched(payload)
    googleTagManager.trackProductImpressionBatched(payload)
  },
)

export const trackProductImpression = whenIdle((payload: EventData) => {
  amplitude.trackProductImpression(payload)
  googleTagManager.trackProductImpression(payload)
})

export const trackProductClick = whenIdle((payload: EventData) => {
  amplitude.trackProductClick(payload)
  googleTagManager.trackProductClick(payload)
})

export const trackErrors = whenIdle((payload: EventData) => {
  amplitude.trackErrors(payload)
})

export const trackLandingPage = whenIdle((payload: EventData) => {
  googleTagManager.trackLandingPage(payload)
})

export const trackViewPromotion = whenIdle(
  googleTagManagerStaging.trackViewPromotion,
)

export const trackLandingBannerImpression = whenIdle((payload: EventData) => {
  amplitude.trackLandingBannerImpression(payload)
  googleTagManager.trackLandingBannerImpression(payload)
  // 🚧 GA4 migration (BB-19): also fire view_promotion staging event for validation
  googleTagManagerStaging.trackViewPromotion({
    creativeName: String(payload.creative ?? ''),
    creativeSlot: String(payload.position ?? ''),
  })
})

export const trackLandingBannerClick = whenIdle((payload: EventData) => {
  amplitude.trackLandingBannerClick(payload)
  googleTagManager.trackLandingBannerClick(payload)
  // 🚧 During GTM migration, also log to console from staging implementation for validation before connecting to real GTM.
  googleTagManagerStaging.trackSelectPromotion({
    creativeName: String(payload.creative ?? ''),
    creativeSlot: String(payload.position ?? ''),
  })
})

export const trackRecyclingProduct = whenIdle((payload: EventData) => {
  amplitude.trackRecyclingProduct(payload)
})

export const trackSwapOffer = whenIdle((payload: EventData) => {
  amplitude.trackSwapOffer(payload)
})

export const trackResaleOffer = whenIdle((payload: EventData) => {
  amplitude.trackResaleOffer(payload)
})

export const trackRecyclingOrder = whenIdle((payload: EventData) => {
  amplitude.trackRecyclingOrder(payload)
  braze.trackRecyclingOrder()
})

export const trackAddToCart = whenIdle((payload: Record<string, unknown>) => {
  amplitude.trackAddToCart(payload as EventData)
  googleTagManager.trackAddToCart(payload as EventData)
  braze.trackAddToCart(payload as EventData)
})

export const trackReplaceInCart = whenIdle(
  (payload: Record<string, unknown>) => {
    amplitude.trackReplaceInCart(payload as EventData)
  },
)

export const trackAddToCartBatch = whenIdle(
  ({
    products,
    list,
  }: {
    products: Record<string, unknown>[]
    list: string
  }) => {
    amplitude.trackAddToCartBatch({ products: products as EventData[] })
    googleTagManager.trackAddToCartBatch({
      products: products as EventData[],
      list,
    })
  },
)

export const trackCarouselClick = whenIdle((payload: EventData) => {
  amplitude.trackCarouselClick(payload)
  googleTagManager.trackCarouselClick(payload)
})

export const trackCarouselImpression = whenIdle((payload: CarouselData) => {
  amplitude.trackCarouselImpression(payload)
  googleTagManager.trackCarouselImpression(payload)
})

export const trackPaginationClicked = whenIdle((payload: EventData) => {
  amplitude.trackPaginationClicked(payload)
})

export const trackProductPage = whenIdle((payload: EventData) => {
  amplitude.trackProduct(payload)
  googleTagManager.trackProduct(payload)
  braze.trackProduct(payload)
})

export const trackInsurance = whenIdle((payload: EventData) => {
  amplitude.trackInsurance(payload)
})

export const trackProductBlockImpression = whenIdle((payload: EventData) => {
  amplitude.trackProductBlockImpression(payload)
})

export const trackBlockImpression = whenIdle((payload: EventData) => {
  amplitude.trackBlockImpression(payload)
})

export const trackToastView = whenIdle((payload: EventData) => {
  amplitude.trackToastView(payload)
})

export const trackAddToMyFavorites = whenIdle((payload: EventData) => {
  amplitude.trackAddToMyFavorites(payload)
  braze.trackAddToMyFavorites(payload)
})

export const trackFavoritesPage = whenIdle(
  ({ pageType, favorites }: { pageType: string; favorites: EventData[] }) => {
    amplitude.trackFavoritesPage({ pageType, favorites })
    braze.trackFavoritesPage({ favorites })
  },
)

export const trackFunnel = whenIdle(
  ({
    pageType,
    products,
    step,
    swap,
    swapCategory,
    swapEstimation,
  }: {
    pageType: string
    products: EventData[]
    step: number | null
    swap: boolean
    swapCategory: string | null
    swapEstimation: number | null
  }) => {
    amplitude.trackFunnel({
      products,
      pageType,
      swap,
      swapCategory,
      swapEstimation,
    })
    googleTagManager.trackFunnel({ products, step, swap })
  },
)

export const trackPaymentResultSuccess = whenIdle(
  ({ payment, products }: { payment: EventData; products: EventData[] }) => {
    amplitude.trackPaymentResultSuccess({ payment, products })
    googleTagManager.trackPaymentResultSuccess({ payment, products })
  },
)

export const trackPaymentResultFailure = whenIdle(
  ({ paymentMethod, errorCode, errorReason }: EventData) => {
    amplitude.trackPaymentResultFailure({
      paymentMethod,
      errorCode,
      errorReason,
    })
    googleTagManager.trackPaymentResultFailure({
      paymentMethod,
      errorCode,
      errorReason,
    })
  },
)

export const trackPostPurchasePayment = whenIdle((payload: EventData) => {
  amplitude.trackPostPurchasePayment(payload)
})

export const trackPromptNotificationImpression = whenIdle(
  (payload: EventData) => {
    googleTagManager.trackPromptNotificationImpression(payload)
    amplitude.trackPromptNotificationImpression(payload)
  },
)

export const trackRemoveFromCart = whenIdle((payload: EventData) => {
  // removeFromCart event (still used by other tracking providers)
  googleTagManager.trackRemoveFromCart(payload)
  amplitude.trackRemoveFromCart(payload)
  // 🚧 During GTM migration, also log to console from staging implementation for validation before connecting to real GTM.
  googleTagManagerStaging.trackRemoveFromCart(payload)
})

export const trackResetPassword = whenIdle(() => {
  googleTagManager.trackResetPassword()
})

export const trackResetPasswordSuccess = whenIdle(() => {
  googleTagManager.trackResetPasswordSuccess()
})

export const trackSellItem = whenIdle((payload: EventData) => {
  amplitude.trackSellItem(payload)
})

export const trackHelpRequestCreated = whenIdle((payload: EventData) => {
  amplitude.trackHelpRequestCreated(payload)
})

export const trackCountrySwitchClick = whenIdle((payload: EventData) => {
  amplitude.trackCountrySwitchClick(payload)
})

export const trackCountrySwitchBannerImpression = whenIdle(
  (payload: EventData) => {
    amplitude.trackCountrySwitchBannerImpression(payload)
  },
)

export const trackComparisonView = whenIdle((payload: EventData) => {
  amplitude.trackComparisonView(payload)
})

export const trackComparisonBlockImpression = whenIdle((payload: EventData) => {
  amplitude.trackComparisonBlockImpression(payload)
})

export const trackComparisonAdd = whenIdle((payload: EventData) => {
  amplitude.trackComparisonAdd(payload)
})

export const trackCompareSectionDisplayed = whenIdle((payload: EventData) => {
  amplitude.trackCompareSectionDisplayed(payload)
})

export const trackCompareProductClicked = whenIdle((payload: EventData) => {
  amplitude.trackCompareProductClicked(payload)
})

export const trackBoSellerListingImpression = whenIdle((payload: EventData) => {
  amplitude.trackBoSellerListingImpression(payload)
})

export const trackFlashSalesErrorImpression = whenIdle((payload: EventData) => {
  amplitude.trackFlashSalesErrorImpression(payload)
})
